import type { ReactNode } from 'react';
import { useRef, useState } from 'react';

interface MediaSliderProps<T> {
  items: T[];
  gapClassName: string;
  gapPx: number;
  fallbackRatio: number;
  renderItem: (item: T, index: number, cardProps: { 'data-slider-card': true }) => ReactNode;
}

export function MediaSlider<T>({
  items,
  gapClassName,
  gapPx,
  fallbackRatio,
  renderItem,
}: MediaSliderProps<T>) {
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  function handleScroll() {
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollLeft + el.clientWidth >= el.scrollWidth - 1) {
      setActiveIndex(items.length - 1);
      return;
    }
    const firstCard = el.querySelector<HTMLElement>('[data-slider-card]');
    const cardStep = firstCard ? firstCard.offsetWidth + gapPx : el.clientWidth * fallbackRatio + gapPx;
    const index = Math.round(el.scrollLeft / cardStep);
    setActiveIndex(Math.min(index, items.length - 1));
  }

  return (
    <>
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className={`flex w-[calc(100%+16px)] min-w-0 touch-pan-x snap-x snap-mandatory scrollbar-none overflow-x-auto ${gapClassName}`}
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        {items.map((item, i) => renderItem(item, i, { 'data-slider-card': true }))}
        <div className="w-4 shrink-0" aria-hidden="true" />
      </div>

      {items.length > 1 && (
        <div className="flex w-16 items-center justify-center gap-2 self-center">
          {items.map((_, i) => (
            <div
              key={i}
              className={`h-px w-4 ${i === activeIndex ? 'bg-jncquoi-neutral-900' : 'bg-jncquoi-neutral-300'}`}
            />
          ))}
        </div>
      )}
    </>
  );
}
